import { computed, ref } from 'vue'
import { backButtonCallback } from '@/router'
import { useConfig } from '@/composables/game/config'
import { usePlayer } from '@/composables/game/player'
import { useNow } from '@/composables/game/time'
import { REWARD_LIVE_OP_CHALLENGE_TYPE, type RewardLiveOp } from '@/types/shared/player-config'
import type { Step, StepReward } from '@/types/shared/pathway-event'

const showPuzzles = ref(false)

export type PuzzlePiece = {
  stepKey: string
  stepNumber: number
  step: Step
  completed: boolean
}

const PUZZLE_PIECE_LIMIT = 12

export const usePuzzles = () => {
  const { config } = useConfig()
  const { player } = usePlayer()
  const { now } = useNow()

  const firstPuzzleLiveOp = computed((): RewardLiveOp | null => {
    const rewardLiveOps = config.value.game.rewardLiveOps
    if (rewardLiveOps.length === 0) {
      return null
    }

    const puzzleLiveOps = rewardLiveOps.filter(op =>
      op.challenge === REWARD_LIVE_OP_CHALLENGE_TYPE.PUZZLE,
    )

    if (puzzleLiveOps.length === 0) {
      return null
    }

    return puzzleLiveOps.find(op => {
      if (op.start > now.value || op.end < now.value) {
        return false
      }

      const progress = player.value.rewardLiveOps.runningEvents[op.id]
      if (progress?.claimed) {
        return false
      }

      // Return first active, unclaimed puzzle
      return true
    }) || null
  })

  const countdownForEventEnd = computed(() => {
    if (!firstPuzzleLiveOp.value) {
      return 0
    }

    return Math.max(firstPuzzleLiveOp.value.end - now.value, 0)
  })

  const puzzleGrid = computed((): PuzzlePiece[] => {
    if (!firstPuzzleLiveOp.value) {
      return []
    }

    const playerProgress = player.value.rewardLiveOps.runningEvents[firstPuzzleLiveOp.value.id]
    const completedSteps = playerProgress?.completedSteps ?? []

    return Object.entries(firstPuzzleLiveOp.value.config.steps).map(([stepKey, step], index) => {
      const stepNumber = index + 1

      return {
        stepKey,
        stepNumber,
        step,
        completed: completedSteps.includes(stepNumber),
      }
    }).slice(0, PUZZLE_PIECE_LIMIT)
  })

  const completedPuzzles = computed(() =>
    puzzleGrid.value.filter(puzzle => puzzle.completed).length,
  )

  const totalPuzzles = computed(() => puzzleGrid.value.length)

  const totalRewards = computed<Partial<StepReward>>(() => {
    if (!firstPuzzleLiveOp.value) {
      return {
        goldenCoins: 0,
        spins: 0,
        giveawayTickets: 0,
        dropeeStars: 0,
        specialCurrencies: {},
      }
    }

    const configReward = firstPuzzleLiveOp.value.config.reward
    return {
      goldenCoins: configReward.goldenCoins || 0,
      spins: configReward.spins || 0,
      giveawayTickets: configReward.giveawayTickets || 0,
      dropeeStars: configReward.dropeeStars || 0,
      specialCurrencies: configReward.specialCurrencies,
    }
  })

  const puzzleBackgroundImage = computed(() => {
    return firstPuzzleLiveOp.value?.image || null
  })

  const isPuzzleRewardClaimed = computed(() => {
    if (!firstPuzzleLiveOp.value) return
    return player.value.rewardLiveOps.runningEvents[firstPuzzleLiveOp.value.id]?.claimed
  })

  const showPuzzle = computed(() => {
    return Boolean(firstPuzzleLiveOp.value) && !isPuzzleRewardClaimed.value
  })

  return {
    firstPuzzleLiveOp,
    puzzleGrid,
    puzzleBackgroundImage,
    completedPuzzles,
    totalPuzzles,
    totalRewards,
    countdownForEventEnd,
    isPuzzleRewardClaimed,
    showPuzzle,
  }
}

export const usePuzzlesOverlay = () => {
  const showPuzzlesOverlay = computed(() => showPuzzles.value)

  function hidePuzzlesOverlay() {
    backButtonCallback.value = null
    showPuzzles.value = false
  }

  function openPuzzlesOverlay() {
    backButtonCallback.value = hidePuzzlesOverlay
    showPuzzles.value = true
  }

  return {
    showPuzzlesOverlay,
    hidePuzzlesOverlay,
    openPuzzlesOverlay,
  }
}